import React, { useState } from 'react';
import {
  ExclamationTriangleIcon,
  FireIcon,
  ClockIcon,
  PlayIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  DiseaseWorkflow,
  WorkflowStatistics,
} from '../../api/diseaseWorkflows';
import WorkflowDetailPanel from './WorkflowDetailPanel';

interface HealthWorkflowsPanelProps {
  activeWorkflows: DiseaseWorkflow[];
  overdueWorkflows: DiseaseWorkflow[];
  workflowStats: WorkflowStatistics | null;
  birds: Array<{ id: string; bandId?: string; species?: { commonName?: string } }>;
  onRefresh: () => Promise<void>;
  onStartInvestigation?: (birdId: string, birdLabel: string) => void;
}

function severityBadge(severity: string) {
  switch (severity) {
    case 'CRITICAL':
      return 'bg-red-200 text-red-900';
    case 'HIGH':
      return 'bg-orange-100 text-orange-800';
    case 'MEDIUM':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-cyan-100 text-cyan-800';
  }
}

function birdLabel(workflow: DiseaseWorkflow, birds: HealthWorkflowsPanelProps['birds']) {
  if (workflow.bird?.bandId) {
    const species = workflow.bird.species?.commonName;
    return species ? `${workflow.bird.bandId} · ${species}` : workflow.bird.bandId;
  }
  const match = birds.find((b) => b.id === workflow.birdId);
  if (match?.bandId) {
    return match.species?.commonName ? `${match.bandId} · ${match.species.commonName}` : match.bandId;
  }
  return workflow.birdId.slice(0, 8) + '…';
}

function WorkflowListItem({
  workflow,
  selected,
  overdue,
  birds,
  onSelect,
}: {
  workflow: DiseaseWorkflow;
  selected: boolean;
  overdue?: boolean;
  birds: HealthWorkflowsPanelProps['birds'];
  onSelect: () => void;
}) {
  const currentStep = workflow.workflowSteps?.find((s) => !s.completed);
  const progress = workflow.progressPercentage ?? 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-lg border p-3 text-left transition ${
        selected
          ? 'border-cyan-400 bg-cyan-50 ring-2 ring-cyan-200'
          : 'border-gray-200 bg-white hover:border-cyan-200 hover:bg-cyan-50/30'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {workflow.severityLevel === 'CRITICAL' ? (
              <FireIcon className="h-4 w-4 shrink-0 text-red-500" />
            ) : (
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-500" />
            )}
            <span className="truncate font-medium text-slate-900">{workflow.diseaseName}</span>
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${severityBadge(workflow.severityLevel)}`}>
              {workflow.severityLevel}
            </span>
            {overdue && (
              <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-800">
                Overdue
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-slate-600">{birdLabel(workflow, birds)}</p>
          {currentStep && (
            <p className="mt-1 truncate text-xs text-cyan-700">Next: {currentStep.title}</p>
          )}
          <div className="mt-2 flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
              <div className="h-full rounded-full bg-cyan-600" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-[10px] text-slate-500">{progress}%</span>
          </div>
        </div>
        <ChevronRightIcon className={`mt-1 h-4 w-4 shrink-0 ${selected ? 'text-cyan-600' : 'text-slate-300'}`} />
      </div>
    </button>
  );
}

export default function HealthWorkflowsPanel({
  activeWorkflows,
  overdueWorkflows,
  workflowStats,
  birds,
  onRefresh,
  onStartInvestigation,
}: HealthWorkflowsPanelProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pickerBirdId, setPickerBirdId] = useState('');

  const overdueIds = new Set(overdueWorkflows.map((w) => w.id));
  const allWorkflows = [
    ...overdueWorkflows,
    ...activeWorkflows.filter((w) => !overdueIds.has(w.id)),
  ];

  const effectiveSelectedId =
    selectedId && allWorkflows.some((w) => w.id === selectedId) ? selectedId : allWorkflows[0]?.id ?? null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <p className="text-sm text-slate-500">Active</p>
          <p className="text-2xl font-semibold text-slate-900">{workflowStats?.active ?? activeWorkflows.length}</p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <p className="text-sm text-slate-500">Overdue</p>
          <p className="text-2xl font-semibold text-orange-600">{workflowStats?.overdue ?? overdueWorkflows.length}</p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <p className="text-sm text-slate-500">Completed</p>
          <p className="text-2xl font-semibold text-green-600">{workflowStats?.completed ?? 0}</p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <p className="text-sm text-slate-500">Total</p>
          <p className="text-2xl font-semibold text-slate-900">{workflowStats?.total ?? allWorkflows.length}</p>
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-cyan-200 bg-cyan-50/50 p-4">
        <h3 className="text-sm font-medium text-slate-900">New care workflow</h3>
        <p className="mt-1 text-sm text-slate-600">
          Start with observed symptoms — the system ranks likely diseases before you commit to a workflow.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <select
            value={pickerBirdId}
            onChange={(e) => setPickerBirdId(e.target.value)}
            className="input text-sm"
          >
            <option value="">Select bird</option>
            {birds.map((bird) => (
              <option key={bird.id} value={bird.id}>
                {bird.bandId || bird.id} {bird.species?.commonName ? `· ${bird.species.commonName}` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!pickerBirdId || !onStartInvestigation}
            onClick={() => {
              const bird = birds.find((b) => b.id === pickerBirdId);
              if (bird && onStartInvestigation) {
                onStartInvestigation(bird.id, bird.bandId || bird.species?.commonName || bird.id);
              }
            }}
            className="btn btn-primary text-sm"
          >
            <PlayIcon className="h-4 w-4" />
            Investigate symptoms
          </button>
        </div>
      </div>

      {allWorkflows.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-slate-50/80 py-10 text-center">
          <p className="text-sm text-slate-500">No active disease workflows. Start one when symptoms are detected.</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-5">
          <div className="space-y-3 lg:col-span-2">
            {overdueWorkflows.length > 0 && (
              <div>
                <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-orange-800">
                  <ClockIcon className="h-4 w-4" />
                  Overdue ({overdueWorkflows.length})
                </h3>
                <div className="space-y-2">
                  {overdueWorkflows.map((workflow) => (
                    <WorkflowListItem
                      key={workflow.id}
                      workflow={workflow}
                      selected={effectiveSelectedId === workflow.id}
                      overdue
                      birds={birds}
                      onSelect={() => setSelectedId(workflow.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-700">
                Active ({activeWorkflows.length})
              </h3>
              <div className="space-y-2">
                {activeWorkflows
                  .filter((w) => !overdueIds.has(w.id))
                  .map((workflow) => (
                    <WorkflowListItem
                      key={workflow.id}
                      workflow={workflow}
                      selected={effectiveSelectedId === workflow.id}
                      birds={birds}
                      onSelect={() => setSelectedId(workflow.id)}
                    />
                  ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 lg:min-h-[560px]">
            <WorkflowDetailPanel
              workflowId={effectiveSelectedId}
              onRefresh={onRefresh}
            />
          </div>
        </div>
      )}
    </div>
  );
}
